import {Box, Button, Divider, FormControlLabel, Switch, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import {useProfile} from 'crqlar-react-common/dist/utils/hooks/profile/useProfiles';
import {PaymentMethod} from 'crqlar-react-common/dist/models/billing/paymentMethod.model';
import {PaymentMethodService} from 'crqlar-react-common/dist/services/billing/paymentMethod.service';
import {useQuery} from 'react-query';
import ProfileInfoRow from 'crqlar-react-common/dist/components/ProfileInfoRow';
import {Folio} from "crqlar-react-common/models/billing/folio.model";
import {FOLIO_FIELD_GAP} from "../editor/folioStyles";

interface HobexProviderProps {
    onDataChange: (isValid: boolean, data: any) => void;
    profileId?: string;
    folio?: Folio;
    /** The folio dialog's payment block: small fields, 13px text. */
    compact?: boolean;
}

const HobexProvider = ({onDataChange, profileId: initialProfileId, folio, compact}: HobexProviderProps) => {
    const {t} = useTranslation();
    const fieldSize = compact ? 'small' : undefined;
    const textSx = compact ? {fontSize: 13} : undefined;
    const [selectedProfileId, setSelectedProfileId] = useState<string | undefined>(initialProfileId);
    const profile = useProfile(selectedProfileId);

    // States for form data
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
    const [useProfilePaymentMethod, setUseProfilePaymentMethod] = useState<boolean>(true);

    // Update local profile state when the prop changes
    useEffect(() => {
        setSelectedProfileId(initialProfileId);
    }, [initialProfileId]);

    // Fetch payment methods from the folio
    const folioPaymentMethodsQuery = useQuery(
        ['folioPaymentMethods', folio?.id, 'hobex'],
        async () => {
            if (!folio?.id) return [];

            const response = await PaymentMethodService.getAllPaymentMethods({
                folioIds: [folio?.id],
                providers: ['hobex'],
                limit: {
                    offset: 0,
                    limit: 100,
                }
            });

            return response.content || [];
        },
        {
            enabled: !!folio?.id,
        }
    );

    // Fetch payment methods for this profile
    const profilePaymentMethodsQuery = useQuery(
        ['paymentMethods', selectedProfileId, 'hobex'],
        async () => {
            if (!selectedProfileId) return [];

            const response = await PaymentMethodService.getAllPaymentMethods({
                profileIds: [selectedProfileId],
                providers: ['hobex'],
                limit: {
                    offset: 0,
                    limit: 100,
                }
            });

            return response.content || [];
        },
        {
            enabled: !!selectedProfileId && (!folioPaymentMethodsQuery.data || folioPaymentMethodsQuery.data.length === 0 || !useProfilePaymentMethod),
        }
    );

    // Use folio payment methods or fall back to profile payment methods
    const folioPaymentMethods = folioPaymentMethodsQuery.data || [];
    const profilePaymentMethods = profilePaymentMethodsQuery.data || [];
    const paymentMethods = useProfilePaymentMethod && folioPaymentMethods.length > 0
        ? folioPaymentMethods
        : profilePaymentMethods;

    // Determine if we're loading payment methods
    const isLoadingPaymentMethods =
        (useProfilePaymentMethod && folioPaymentMethodsQuery.isLoading) ||
        (!useProfilePaymentMethod && profilePaymentMethodsQuery.isLoading);

    // Reset payment method when sources change
    useEffect(() => {
        setSelectedPaymentMethod(undefined);
    }, [selectedProfileId, useProfilePaymentMethod]);

    // Auto-select the first folio payment method if there's only one
    useEffect(() => {
        if (folioPaymentMethods.length === 1 && useProfilePaymentMethod) {
            setSelectedPaymentMethod(folioPaymentMethods[0]);
        }
    }, [folioPaymentMethods, useProfilePaymentMethod]);

    // Validate form and pass data to parent
    useEffect(() => {
        const isValid = (!!selectedPaymentMethod || paymentMethods.length === 0);

        const data = {
            paymentMethodId: selectedPaymentMethod?.id,
            provider: 'hobex',
            profileId: selectedProfileId
        };

        onDataChange(isValid, data);
    }, [
        selectedPaymentMethod,
        selectedProfileId,
        onDataChange,
        paymentMethods.length
    ]);

    return (
        <Box
            sx={{
                gap: compact ? FOLIO_FIELD_GAP : '12px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {folioPaymentMethods.length > 0 && (
                <FormControlLabel
                    control={
                        <Switch
                            size={fieldSize}
                            checked={useProfilePaymentMethod}
                            onChange={(e) => setUseProfilePaymentMethod(e.target.checked)}
                        />
                    }
                    label={t('Use payment methods linked to this folio')}
                    componentsProps={compact ? {typography: textSx} : undefined}
                />
            )}

            {/*{(!useProfilePaymentMethod || folioPaymentMethods.length === 0) && (*/}
            {/*    <>*/}
            {/*        <ProfileInfoRow*/}
            {/*            id={'hobex-profile-selector'}*/}
            {/*            title={t('Profile')}*/}
            {/*            profileId={selectedProfileId}*/}
            {/*            setProfileId={setSelectedProfileId}*/}
            {/*            onDelete={() => setSelectedProfileId(undefined)}*/}
            {/*        />*/}

            {/*        <Divider sx={{my: 1}}/>*/}
            {/*    </>*/}
            {/*)}*/}

            {!useProfilePaymentMethod && !selectedProfileId ? (
                <Typography color="warning.main" sx={textSx}>
                    {t('Please select a profile to see available payment methods')}
                </Typography>
            ) : isLoadingPaymentMethods ? (
                <Typography variant="body2">{t('Loading payment methods...')}</Typography>
            ) : paymentMethods.length > 0 ? (
                <TextField
                    select
                    fullWidth
                    size={fieldSize}
                    label={t('Payment method')}
                    value={selectedPaymentMethod?.id || ''}
                    onChange={(e) => {
                        const selectedId = e.target.value;
                        setSelectedPaymentMethod(paymentMethods.find(pm => pm.id === selectedId));
                    }}
                    SelectProps={{
                        native: true,
                    }}
                    // A native select shows the empty option's text: in the new dialog the label stays
                    // above it (the legacy dialogs keep theirs as they were)
                    InputLabelProps={compact ? {shrink: true} : undefined}
                >
                    <option value="">{t('Select a payment method')}</option>
                    {paymentMethods.map((pm) => (
                        <option key={pm.id} value={pm.id}>
                            **** **** **** {pm.card?.last4} ({pm.card?.expMonth}/{pm.card?.expYear})
                        </option>
                    ))}
                </TextField>
            ) : (
                <Typography color="warning.main" sx={textSx}>
                    {useProfilePaymentMethod
                        ? t('No Hobex payment methods linked to this folio. Try using profile payment methods instead.')
                        : t('No Hobex payment methods available for this profile')}
                </Typography>
            )}

            {useProfilePaymentMethod && folioPaymentMethods.length === 0 && (
                <Button
                    variant="text"
                    color="primary"
                    size={fieldSize}
                    onClick={() => setUseProfilePaymentMethod(false)}
                    sx={{alignSelf: 'flex-start'}}
                >
                    {t('Use profile payment methods instead')}
                </Button>
            )}
        </Box>
    );
};

export default HobexProvider;